import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { NavBar } from '../components/NavBar';
import { Footer } from '../components/Footer';
import { CTASection } from '../components/CTASection';
import { ExploreMoreSection } from '../components/ExploreMoreSection';
import { LeadForm } from '../components/LeadForm';
import { MeetingModal } from '../components/MeetingModal';
import { ResponsiveHeroImage } from '../components/ResponsiveHeroImage';
import { Button } from '../components/ui/button';
import { Calendar, Phone, DollarSign, BarChart3, Home } from 'lucide-react';
import { trackEvent, trackPageView } from '@/lib/analytics';
import { getBreadcrumbSchema, getInvestPageSchema, getOrganizationSchema } from '@/lib/schema';
import {
  investIntro,
  investIntroSecondary,
  investIntroPearl,
  investSections,
  investStats,
  investCards,
} from '@/data/investInfo';

export const Invest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);

  const whatsappNumber = '13451234567';
  const whatsappMessage = encodeURIComponent(
    'Hi, I\'m interested in investment opportunities at The Pearl at Grand Harbour. Can you provide more information?'
  );

  useEffect(() => {
    // Track page view
    trackPageView('/invest');

    // Inject JSON-LD structured data
    const schemas = [
      getOrganizationSchema(),
      getBreadcrumbSchema(location.pathname),
      getInvestPageSchema(),
    ];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      script.id = `schema-invest-${index}`;
      document.head.appendChild(script);
    });

    return () => {
      // Cleanup schemas on unmount
      schemas.forEach((_, index) => {
        const script = document.getElementById(`schema-invest-${index}`);
        if (script) script.remove();
      });
    };
  }, [location.pathname]);

  const handleScheduleConsultation = () => {
    setMeetingOpen(true);
  };

  const handleContact = () => {
    setLeadFormOpen(true);
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`, '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Investment Opportunities - The Pearl at Grand Harbour | Real Estate Investment Cayman</title>
        <meta 
          name="description" 
          content="Invest in The Pearl at Grand Harbour. 2025 market fundamentals: 5% condo growth, record $1B transactions, and tightening supply. Prime Grand Harbour location with lifestyle-led rental appeal." 
        />
        <meta 
          name="keywords" 
          content="Cayman Islands real estate investment, Grand Harbour property investment, Caribbean property investment, rental property Cayman, investment property Grand Cayman" 
        />
        <link rel="canonical" href={`${window.location.origin}/invest`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/invest`} />
        <meta property="og:title" content="Investment Opportunities - The Pearl at Grand Harbour" />
        <meta property="og:description" content="Invest in The Pearl at Grand Harbour. 2025 market fundamentals: 5% condo growth, record $1B transactions, and tightening supply in Cayman's premium segment." />
        <meta property="og:image" content={`${window.location.origin}/img/aerial-1.png`} />
        <meta property="og:locale" content="en_KY" />
        <meta property="og:site_name" content="The Pearl at Grand Harbour" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`${window.location.origin}/invest`} />
        <meta name="twitter:title" content="Investment Opportunities - The Pearl at Grand Harbour" />
        <meta name="twitter:description" content="Invest in The Pearl at Grand Harbour. 2025 market fundamentals: 5% condo growth, record $1B transactions, and tightening supply in Cayman's premium segment." />
        <meta name="twitter:image" content={`${window.location.origin}/img/aerial-1.png`} />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Navigation */}
        <NavBar 
          onRegisterClick={() => setLeadFormOpen(true)} 
          onConsultClick={() => setMeetingOpen(true)}
        />

      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <ResponsiveHeroImage
            baseName="aerial-1"
            originalSrc="/img/aerial-1.png"
            alt="The Pearl at Grand Harbour - Investment Opportunity"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-end">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <div className="max-w-4xl">
              <h1 className="font-display text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Invest in The Pearl
              </h1>
              <p className="text-xl lg:text-2xl text-white/90 mb-8 leading-relaxed">
                Lifestyle-led rental appeal and strong location fundamentals make The Pearl a compelling investment opportunity
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-brand-teal hover:bg-brand-navy text-white"
                  onClick={handleScheduleConsultation}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Consultation
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white bg-black/30 hover:bg-white hover:!text-foreground hover:!border-foreground"
                  onClick={handleContact}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Benefits Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground mb-6">
                Why Invest in The Pearl
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                A strategic investment opportunity combining luxury living, prime location, and strong rental potential
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {investCards.map((card) => (
                <div key={card.id} className="group bg-white rounded-lg overflow-hidden shadow-soft hover:shadow-lg transition-shadow duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-xl text-foreground mb-2">{card.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{card.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2025 Market Snapshot */}
      <section className="py-16 lg:py-24 bg-deep-navy text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <p className="text-brand-teal font-semibold text-sm tracking-widest uppercase text-center mb-3">
              Market Data
            </p>
            <h2 className="font-display text-3xl lg:text-4xl font-semibold text-center mb-14">
              2025 Cayman Market Snapshot
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
              {investStats.map((stat) => (
                <div
                  key={stat.label}
                  className="relative text-center bg-white/[0.06] border border-white/10 rounded-xl px-4 py-8 lg:py-10"
                >
                  <p className="font-display text-4xl lg:text-5xl xl:text-6xl font-bold text-brand-teal mb-1 tracking-tight">
                    {stat.value}
                  </p>
                  <div className="w-10 h-0.5 bg-brand-teal/50 mx-auto my-3" />
                  <p className="text-sm lg:text-base text-white/80 font-medium">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Investment Fundamentals Intro + Highlights */}
      <section className="py-16 lg:py-24 bg-soft-cream">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Intro Content */}
              <div>
                <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground mb-6">
                  Investment Fundamentals
                </h2>
                <p className="text-muted-foreground mb-4 leading-relaxed">{investIntro}</p>
                <p className="text-muted-foreground mb-4 leading-relaxed">{investIntroSecondary}</p>
                <p className="text-foreground/80 font-medium leading-relaxed">{investIntroPearl}</p>
              </div>

              {/* Right Column - Highlights Sidebar */}
              <div className="bg-white rounded-lg p-8 shadow-soft">
                <h3 className="font-display text-2xl font-semibold text-foreground mb-6 text-center">
                  Investment Highlights
                </h3>
                <div className="space-y-4">
                  <div className="border-l-4 border-brand-teal pl-4">
                    <p className="font-semibold text-foreground">Premium Segment Alignment</p>
                    <p className="text-sm text-muted-foreground">12% of transactions, ~50% of total sales value</p>
                  </div>
                  <div className="border-l-4 border-brand-teal pl-4">
                    <p className="font-semibold text-foreground">Tightening Supply</p>
                    <p className="text-sm text-muted-foreground">New condo supply declined for a third consecutive year</p>
                  </div>
                  <div className="border-l-4 border-brand-teal pl-4">
                    <p className="font-semibold text-foreground">Location-Driven Performance</p>
                    <p className="text-sm text-muted-foreground">Grand Harbour's connectivity and infrastructure investment</p>
                  </div>
                  <div className="border-l-4 border-brand-teal pl-4">
                    <p className="font-semibold text-foreground">Hands-Off Ownership</p>
                    <p className="text-sm text-muted-foreground">Comprehensive management from tenant placement to asset care</p>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t">
                  <Button 
                    className="w-full bg-brand-teal hover:bg-brand-navy text-white"
                    onClick={handleScheduleConsultation}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Investment Consultation
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alternating Feature Sections */}
      {investSections.map((section, index) => {
        const iconMap: Record<string, React.ReactNode> = {
          'rental-yield': <DollarSign className="w-6 h-6 mr-3 text-brand-teal" />,
          'location': <BarChart3 className="w-6 h-6 mr-3 text-brand-teal" />,
          'management': <Home className="w-6 h-6 mr-3 text-brand-teal" />,
        };
        const isEven = index % 2 === 1;
        return (
          <section
            key={section.id}
            className={`py-16 lg:py-20 ${index % 2 === 0 ? 'bg-background' : 'bg-soft-cream'}`}
          >
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${isEven ? 'lg:[direction:rtl]' : ''}`}>
                  {/* Image Column */}
                  <div className={isEven ? 'lg:[direction:ltr]' : ''}>
                    <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg">
                      <img
                        src={section.image}
                        alt={section.imageAlt}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Text Column */}
                  <div className={isEven ? 'lg:[direction:ltr]' : ''}>
                    <h3 className="font-display text-2xl lg:text-3xl font-semibold text-foreground mb-5 flex items-center">
                      {iconMap[section.id]}
                      {section.title}
                    </h3>
                    {section.content.split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="text-muted-foreground mb-4 last:mb-0 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Next Steps Section - Background Image CTA */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="/img/aerial-1.png"
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/60 to-black/50" />
        </div>

        {/* CTA Content */}
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-3xl lg:text-4xl font-semibold text-white mb-6">
              Ready to Invest?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Schedule a consultation with our investment team to learn more about opportunities, pricing, and the investment process at The Pearl.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-brand-teal hover:bg-brand-navy text-white"
                onClick={handleScheduleConsultation}
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Consultation
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white bg-white/10 hover:bg-white hover:!text-foreground"
                onClick={handleContact}
              >
                <Phone className="w-5 h-5 mr-2" />
                Contact Investment Team
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Explore More Section */}
      <ExploreMoreSection 
        onContactClick={() => setLeadFormOpen(true)}
        currentPage="invest"
      />

      {/* CTA Section */}
      <CTASection 
        onRegisterClick={() => setLeadFormOpen(true)}
        onConsultClick={() => setMeetingOpen(true)}
        onWhatsAppClick={handleWhatsAppClick}
      />

      {/* Footer */}
      <Footer />

      {/* Modals */}
      <LeadForm open={leadFormOpen} onOpenChange={setLeadFormOpen} />
      <MeetingModal
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        onWhatsAppClick={handleWhatsAppClick}
      />
      </div>
    </>
  );
};

